import { ActionIcon, Touchable, View } from '@/components'
import { ClimateEngagementScore, ClimateEngagementScoreProps } from './ClimateEngagementScore'
import { APIClient } from '@/services'
import { OrganisationCategory, ScoreTopic } from '@/types'
import { Theme, variantProvider } from '@/app'
import { interpolate, motion, MotionValue, useMotionValue, useMotionValueEvent, useScroll, useTransform } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { useIsMobile } from '@/utils'
type PerTopicEngagementScoreProps = Omit<ClimateEngagementScoreProps, 'topic'|'onExpand'> & {
    industry?: OrganisationCategory
    onExpand: (topic:ScoreTopic) => void
}

type DotProps = {
    index: number
    progress?: MotionValue<number>
    onPress: () => void

}

const Dot = (props: DotProps) => {
  const { index, progress, onPress, ...rest } = props
  const inRange = [index - 1, index, index + 1]

  const scale = useTransform(progress, inRange, [0.66, 1, 0.66], {
    clamp: true,
  })

  const backgroundColor = useTransform(
    progress,
    inRange,
    [Theme.colors.light.neutral3, Theme.colors.light.primary3, Theme.colors.light.neutral3], {
      clamp: true,
    },
  )

  return <motion.div
    onClick={onPress}
    style={{
      ...styles.dot,
      scale,
      backgroundColor,
    }}

  />
}

function useMotionValueState<T>(value: MotionValue<T>) {
  const [state, setState] = useState(() => {
    return value.get()
  })

  useMotionValueEvent(value, 'change', (v) => {
    setState(v)
  })

  return state
}

export const PerTopicEngagementScore = (props: PerTopicEngagementScoreProps) => {

  const {
    organisation,
    industry,
    onExpand,
    ...otherProps
  } = props

  const topics = APIClient.Organisations.useScoreTopics()

  const topicScores = APIClient.Organisations.useTopicScores({
    topics: topics.data.map(t => t.value),
    category: industry?.slug,
    organisation: organisation?.id,
  })

  const renderItem = (item:ReturnType<typeof APIClient.Organisations.useTopicScores>[number], index:number) => {

    const score = item.data?.score
    const topic = topics?.data?.[index]

    return <View style={{ minWidth: '100%', scrollSnapAlign: 'center' }} key={topic?.value}>
      <ClimateEngagementScore
        score={score}
        variant='modal'
        showReviews={false}
        loading={item.isLoading}
        {...otherProps}
        topic={topic}
        onExpand={() => {
          onExpand(topic)
        }}

      />
    </View>
  }

  const scrollRef = useRef<HTMLDivElement>()

  const { scrollXProgress } = useScroll({
    container: scrollRef,
  })

  const scrollIndex = useTransform(scrollXProgress, [0, 1], [0, topics?.data.length - 1], {
    clamp: true,
  })

  function scrollTo(index:number) {
    const scrollerWidth = scrollRef.current.getBoundingClientRect().width
    scrollRef.current.scrollTo({
      left: scrollerWidth * index,
      behavior: 'smooth',
    })
  }

  const onScrollToItem = (index:number) => {
    return () => {
      scrollTo(index)

    }
  }

  console.log('tt', topics?.data?.length)

  const showScrollLeft = useMotionValueState(useTransform(scrollIndex, (v) => Math.round(v) > 0))
  const showScrollRight = useMotionValueState(useTransform(scrollIndex, (v) => Math.round(v) < topics?.data?.length - 1))

  const isMobile = useIsMobile()

  return <View variants={['column', 'gap:3']} style={{ maxWidth: '100%' }}>
    <motion.div css={styles.scroller} ref={scrollRef} >
      {topicScores.map(renderItem)}
    </motion.div>
    <View variants={['row', 'justifySpaceBetween', 'alignCenter']}>

      {isMobile ? null : (
        <ActionIcon
          debugName='Previous score'
          icon='chevron-left'
          disabled={!showScrollLeft}
          variants={['minimal', 'neutral4', !showScrollLeft && 'hidden']}
          onPress={() => {
            scrollTo(Math.round(scrollIndex.get()) - 1)
          }}
        />
      )}

      <View variants={['row', 'justifyCenter', 'gap:1', 'flex']}>
        {topics?.data?.map((_, index) => <Dot
          key={index}
          progress={scrollIndex}
          index={index}
          onPress={onScrollToItem(index)}

        />)}
      </View>
      {
        isMobile ? null : (
          <ActionIcon
            debugName='Previous score'
            icon='chevron-right'
            variants={['minimal', 'neutral4', !showScrollRight && 'hidden']}
            onPress={() => {
              scrollTo(Math.round(scrollIndex.get()) + 1)
            }}
            disabled={!showScrollRight}
          />

        )
      }
    </View>
  </View>
}

const styles = variantProvider.createComponentStyle(theme => ({
  scroller: {
    ...theme.spacing.gap(2),
    display: 'flex',
    flexDirection: 'row',
    overflowX: 'scroll',
    overflowY: 'hidden',
    maxWidth: '100%',
    scrollSnapType: 'x mandatory',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  dot: {

    width: 12,
    borderRadius: theme.borderRadius.rounded,
    height: 12,
  },
}), true)
